// src/auth/passwordPolicy.js
//
// Client-side password requirements for the two token screens.
//
// ⚠️ UX ONLY, NOT A SECURITY GATE. The backend validates 8–128 characters and
// nothing else (SetPasswordRequest / PasswordResetConfirmRequest in
// q/src/auth/router.py) — there is no complexity policy anywhere in q/src. The
// Upper/Lower/SpecialChar rules below come from issue #34 and are trivially
// bypassed by posting to the API directly. See the plan's "Known discrepancy".

export const PASSWORD_MIN = 8;
export const PASSWORD_MAX = 128;

/**
 * @param {string} pw
 * @returns {{length: boolean, upper: boolean, lower: boolean, special: boolean}}
 */
export function checkPassword(pw) {
  const value = typeof pw === 'string' ? pw : '';
  return {
    length:  value.length >= PASSWORD_MIN && value.length <= PASSWORD_MAX,
    // ⚠️ UNICODE-AWARE ON PURPOSE, and the three must stay consistent with each
    // other. With the ASCII forms (/[A-Z]/, /[a-z]/, /[^A-Za-z0-9]/) every
    // non-ASCII LETTER fell outside upper AND lower and therefore landed in
    // `special` — so on an Italian-market product `Perché2026` satisfied the
    // "special character" rule on the strength of an accent, while `Èlena1!`
    // failed the uppercase one. \p{Lu}/\p{Ll}/\p{L} classify by Unicode
    // category instead, which is what the comment below always claimed.
    upper:   /\p{Lu}/u.test(value),
    lower:   /\p{Ll}/u.test(value),
    // "Special" = anything that is not a letter or a digit, so accented and
    // non-Latin characters are not silently counted as special.
    special: /[^\p{L}\p{N}]/u.test(value),
  };
}

export function isPasswordValid(pw) {
  return Object.values(checkPassword(pw)).every(Boolean);
}

// Fixed order — the requirement checklist renders in this order and each key
// maps to auth:passwordForm.req<Key> copy.
export const PASSWORD_RULES = ['length', 'upper', 'lower', 'special'];
